/* eslint-disable react/sort-prop-types */
import SchemaForm from '@department-of-veterans-affairs/platform-forms-system/SchemaForm';
import { merge } from 'lodash-es';
import { useHeadingLevels } from 'platform/forms-system/src/js/web-component-patterns/titlePattern';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { setData } from '~/platform/forms-system/src/js/actions';
import FormNavButtons from '~/platform/forms-system/src/js/components/FormNavButtons';
import get from '~/platform/utilities/data/get';
import { isNavButtonAnalyticsEnabled } from 'platform/forms-system/src/js/helpers';
import ArrayBuilderCards from './ArrayBuilderCards';
import ArrayBuilderSummaryNoSchemaFormPage from './ArrayBuilderSummaryNoSchemaFormPage';
import ArrayBuilderSummaryReviewPage from './ArrayBuilderSummaryReviewPage';
import {
  arrayBuilderContextObject,
  checkIfArrayHasDuplicateData,
  createArrayBuilderItemAddPath,
  getEffectiveIsItemIncomplete,
  getUpdatedItemFromPath,
  // META_DATA_KEY,
  maxItemsFn,
  slugifyText,
  withUrlPrefix,
} from './helpers';
import { useEnforceYesNo } from './useEnforceYesNo';
import { useSummaryPageAlerts } from './useSummaryPageAlerts';
import { useSummaryPageInit } from './useSummaryPageInit';

function checkHasYesNoReviewError(reviewErrors, hasItemsKey) {
  return (
    hasItemsKey && reviewErrors?.errors?.some(obj => obj.name === hasItemsKey)
  );
}

/**
 * @param {{
 *   arrayPath: ArrayBuilderOptions['arrayPath'],
 *   getFirstItemPagePath: (formData, index, context) => string,
 *   getText: ArrayBuilderGetText,
 *   hasItemsKey: string,
 *   hideMaxItemsAlert: boolean,
 *   getIntroPath: (formData) => string,
 *   isItemIncomplete: ArrayBuilderOptions['isItemIncomplete'],
 *   isReviewPage: boolean,
 *   maxItems: ArrayBuilderOptions['maxItems'],
 *   nounPlural: ArrayBuilderOptions['nounPlural'],
 *   nounSingular: ArrayBuilderOptions['nounSingular'],
 *   required: (formData) => boolean,
 *   titleHeaderLevel: string,
 *   useLinkInsteadOfYesNo: ArrayBuilderOptions['useLinkInsteadOfYesNo'],
 *   useButtonInsteadOfYesNo: ArrayBuilderOptions['useButtonInsteadOfYesNo'],
 *   canAddItem: ArrayBuilderOptions['canAddItem'],
 *   canEditItem: ArrayBuilderOptions['canEditItem'],
 *   canDeleteItem: ArrayBuilderOptions['canDeleteItem'],
 *   duplicateChecks: ArrayBuilderOptions['duplicateChecks'],
 * }} arrayBuilderOptions
 * @returns {CustomPageType}
 */
export default function ArrayBuilderSummaryPage(arrayBuilderOptions) {
  const {
    arrayPath,
    getFirstItemPagePath,
    getText,
    hasItemsKey,
    hideMaxItemsAlert,
    isItemIncomplete,
    isReviewPage,
    nounSingular,
    required,
    titleHeaderLevel,
    useLinkInsteadOfYesNo,
    useButtonInsteadOfYesNo,
    canEditItem,
    canDeleteItem,
    canAddItem,
    duplicateChecks = {},
  } = arrayBuilderOptions;

  // use closure variable rather than useRef to
  // maintain state over page navigations and review page.
  let maxItemsAlertSeen = false;

  /** @type {CustomPageType} */
  function CustomPage(props) {
    const { index: updateItemIndex, arrayPathSlug: updatedArrayPathSlug } =
      getUpdatedItemFromPath();
    const arrayData = get(arrayPath, props.data);
    const arrayPathSlug = slugifyText(arrayPath);
    const updatedItemData =
      updatedArrayPathSlug === arrayPathSlug && updateItemIndex != null
        ? arrayData?.[updateItemIndex]
        : null;
    const effectiveIsItemIncomplete = getEffectiveIsItemIncomplete({
      arrayPath,
      isItemIncomplete,
      formPages: props.formPages,
      appStateData: props.appStateData,
    });

    const dataRef = useRef(props.data);
    const { uiSchema, schema } = props;
    const { headingLevel, headingClasses } = useHeadingLevels({
      userHeaderLevel: titleHeaderLevel,
      isReviewPage,
    });
    const Heading = `h${headingLevel}`;
    const maxItems = maxItemsFn(arrayBuilderOptions.maxItems, props.data);
    const isMaxItemsReached = arrayData?.length >= maxItems;
    const hasReviewError =
      isReviewPage && checkHasYesNoReviewError(props.reviewErrors, hasItemsKey);

    const duplicateCheckResult = checkIfArrayHasDuplicateData({
      arrayPath,
      duplicateChecks,
      fullData: props.fullData,
    });

    const setDataFromRef = data => {
      const dataToSet = { ...(dataRef.current || {}), ...data };
      dataRef.current = dataToSet;
      props.setData(dataToSet);
    };

    useEffect(() => {
      dataRef.current = props.data;
    }, [props.data]);

    useSummaryPageInit({
      arrayBuilderProps: arrayBuilderOptions,
      customPageProps: props,
      dataRef,
      setData: setDataFromRef,
    });

    useEnforceYesNo({
      arrayBuilderProps: arrayBuilderOptions,
      customPageProps: props,
      dataRef,
      setData: setDataFromRef,
    });

    const {
      renderAlerts,
      showUpdatedAlert,
      showRemovedAlert,
      showReviewErrorAlert,
      showRemovedItemAlert,
    } = useSummaryPageAlerts({
      arrayBuilderOptions,
      customPageProps: props,
      isMaxItemsReached,
      arrayData,
      updatedItemData,
      updateItemIndex,
      updatedArrayPathSlug,
      arrayPathSlug,
      hasReviewError,
      getMaxItemsAlertSeen: () => maxItemsAlertSeen,
      setMaxItemsAlertSeen: seen => {
        maxItemsAlertSeen = seen;
      },
    });

    const addAnotherItemIndex = arrayData ? arrayData.length : 0;
    const addAnotherItemPath = createArrayBuilderItemAddPath({
      path: getFirstItemPagePath(
        props.data,
        addAnotherItemIndex,
        arrayBuilderContextObject({
          add: true,
          review: isReviewPage,
        }),
      ),
      index: addAnotherItemIndex,
      isReview: isReviewPage,
      removedAllWarn: !arrayData?.length && required(props.data),
    });
    const addAnotherItemFullPath = withUrlPrefix(
      addAnotherItemPath,
      props.urlPrefix,
    );

    function addAnotherItemButtonClick(chapterKey = null) {
      props.goToPath(addAnotherItemFullPath, {
        force: true,
        chapterKey,
      });
    }

    function onRemoveItem(index, item, newFormData) {
      const onUpdate = isReviewPage ? props.setData : props.onChange;
      onUpdate(newFormData);
      showRemovedItemAlert(
        getText('alertItemDeleted', item, props.data, index),
        index,
      );
    }

    function onRemoveAllItems(newFormData) {
      if (required(props.data)) {
        const path = createArrayBuilderItemAddPath({
          path: getFirstItemPagePath(newFormData, 0, {
            add: true,
            review: isReviewPage,
          }),
          index: 0,
          isReview: isReviewPage,
          removedAllWarn: true,
        });

        props.goToPath(withUrlPrefix(path, props.urlPrefix), {
          force: true,
        });
      }
    }

    const renderTitle = textType => {
      const text = getText(textType, updatedItemData, props.data);
      return text ? (
        <Heading
          className={headingClasses}
          data-title-for-noun-singular={nounSingular}
          data-array-path={arrayPath}
        >
          {text}
        </Heading>
      ) : null;
    };

    const renderCards = () => (
      <ArrayBuilderCards
        arrayPath={arrayPath}
        nounSingular={nounSingular}
        isIncomplete={effectiveIsItemIncomplete}
        getEditItemPathUrl={getFirstItemPagePath}
        urlPrefix={props.urlPrefix}
        getText={getText}
        required={required}
        onRemoveAll={onRemoveAllItems}
        onRemove={onRemoveItem}
        isReview={isReviewPage}
        titleHeaderLevel={headingLevel}
        fullData={props.fullData}
        canEditItem={canEditItem}
        canDeleteItem={canDeleteItem}
        duplicateChecks={duplicateChecks}
        duplicateCheckResult={duplicateCheckResult}
      />
    );

    // Calculate hideAdd based on maxItems and canAddItem
    const canAddItemCheck =
      typeof canAddItem !== 'function' ||
      canAddItem({ arrayData, fullData: props.data, isReview: isReviewPage });
    const hideAdd = isMaxItemsReached || !canAddItemCheck;

    if (isReviewPage) {
      return (
        <ArrayBuilderSummaryReviewPage
          customPageProps={props}
          arrayBuilderOptions={arrayBuilderOptions}
          arrayData={arrayData}
          addAnotherItemButtonClick={addAnotherItemButtonClick}
          addAnotherItemPath={addAnotherItemFullPath}
          updatedItemData={updatedItemData}
          alerts={renderAlerts()}
          cards={renderCards()}
          title={renderTitle('summaryTitle')}
          hideAdd={hideAdd}
        />
      );
    }

    const NavButtons = props.NavButtons || FormNavButtons;
    const hasArrayItems = !!arrayData?.length;

    // ui:title and ui:description are React nodes built below in render
    // scope, so they're assigned onto newUiSchema after this call.
    let newSchema = schema;
    const newUiSchema = { ...uiSchema };

    const typeSuffix = hasArrayItems ? '' : 'WithoutItems';
    const titleTextType = `summaryTitle${typeSuffix}`;
    const descriptionTextType = `summaryDescription${typeSuffix}`;

    const showMaxItemsAlert = isMaxItemsReached && !hideMaxItemsAlert;
    const hasVisibleDescriptionContent =
      hasArrayItems ||
      showUpdatedAlert ||
      showRemovedAlert ||
      showReviewErrorAlert ||
      showMaxItemsAlert;

    const hasNegativeMarginTop =
      !hasVisibleDescriptionContent &&
      !uiSchema?.['ui:options']?.forceDivWrapper &&
      !useLinkInsteadOfYesNo &&
      !useButtonInsteadOfYesNo;

    const renderKey = [
      showUpdatedAlert ? 'u1' : 'u0',
      showRemovedAlert ? 'r1' : 'r0',
      showReviewErrorAlert ? 'e1' : 'e0',
      isMaxItemsReached ? 'm1' : 'm0',
    ].join('-');
    const UIDescription = (
      <div
        key={renderKey}
        className={hasNegativeMarginTop ? 'vads-u-margin-top--neg3' : ''}
      >
        {renderAlerts()}
        {hasArrayItems && renderCards()}
      </div>
    );

    const descriptionText = getText(descriptionTextType, null, props.data);
    const UITitle = (
      <>
        {renderTitle(titleTextType)}
        {descriptionText && (
          <span className="vads-u-font-family--sans vads-u-font-weight--normal vads-u-display--block">
            {descriptionText}
          </span>
        )}
      </>
    );

    newUiSchema['ui:title'] = UITitle;
    newUiSchema['ui:description'] = UIDescription;

    if (newSchema.properties[hasItemsKey] && hideAdd) {
      newSchema = merge({}, newSchema, {
        properties: { [hasItemsKey]: { 'ui:hidden': true } },
      });
    }

    if (useLinkInsteadOfYesNo || useButtonInsteadOfYesNo) {
      return (
        <ArrayBuilderSummaryNoSchemaFormPage
          title={UITitle}
          description={UIDescription}
          arrayData={arrayData}
          hideAdd={hideAdd}
          customPageProps={props}
          arrayBuilderOptions={arrayBuilderOptions}
          addAnotherItemButtonClick={addAnotherItemButtonClick}
          isItemIncomplete={isItemIncomplete}
          NavButtons={NavButtons}
        />
      );
    }

    return (
      <SchemaForm
        name={props.name}
        title={props.title}
        data={props.data}
        appStateData={props.appStateData}
        schema={newSchema}
        uiSchema={newUiSchema}
        pagePerItemIndex={props.pagePerItemIndex}
        formContext={props.formContext}
        trackingPrefix={props.trackingPrefix}
        onChange={props.onChange}
        onSubmit={props.onSubmit}
        formOptions={props.formOptions}
      >
        <>
          {/* contentBeforeButtons = save-in-progress links */}
          {props.pageContentBeforeButtons}
          {props.contentBeforeButtons}
          <NavButtons
            goBack={props.goBack}
            goForward={props.onContinue}
            submitToContinue
            useWebComponents={props.formOptions?.useWebComponentForNavigation}
            enableAnalytics={isNavButtonAnalyticsEnabled(props.formOptions)}
          />
          {props.contentAfterButtons}
        </>
      </SchemaForm>
    );
  }

  CustomPage.propTypes = {
    name: PropTypes.string.isRequired,
    schema: PropTypes.object,
    uiSchema: PropTypes.object,
    appStateData: PropTypes.object,
    chapterKey: PropTypes.string,
    contentAfterButtons: PropTypes.node,
    contentBeforeButtons: PropTypes.node,
    data: PropTypes.object,
    formContext: PropTypes.object,
    formOptions: PropTypes.object,
    formPages: PropTypes.object,
    fullData: PropTypes.object,
    goBack: PropTypes.func,
    goToPath: PropTypes.func,
    urlPrefix: PropTypes.string,
    onChange: PropTypes.func,
    onContinue: PropTypes.func,
    onReviewPage: PropTypes.bool,
    onSubmit: PropTypes.func,
    pageContentBeforeButtons: PropTypes.node,
    pagePerItemIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recalculateErrors: PropTypes.func,
    reviewErrors: PropTypes.object,
    setData: PropTypes.func, // available regardless of review page or not
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    trackingPrefix: PropTypes.string,
    NavButtons: PropTypes.func,
  };

  const mapStateToProps = state => ({
    reviewErrors: state?.form?.formErrors,
    formPages: state?.form?.pages,
  });

  const mapDispatchToProps = {
    setData,
  };

  CustomPage.displayName = 'ArrayBuilderSummaryPageCustom';
  return connect(mapStateToProps, mapDispatchToProps)(CustomPage);
}
